import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import MatchAccaSelectionItem from 'modules/match-acca/match-acca-selection-item/index.js';
import BetslipModuleUtils from 'modules/betslip/utils.js';
import SelectionButton from 'modules/selection-button/index.js';
import PriceBoostBanner from 'modules/price-boost-banner/index.js';
import SelectionsContext from 'modules/selection-button/context/selections-context.js';
import PrebuiltBetSelectionsContext from 'modules/prebuilt-bets/prebuilt-bet/context/prebuilt-bet-selections-context.js';
import ShowAllSelectionsButton from 'modules/aggregated-market-pane/show-all-selections-button';

import { useMemo, memo, useState } from 'react';
import { useSelector } from 'react-redux';

const { FlexPane, SportWatermarkImage, Button } = MojitoPresentation.Components;
const { types: BetslipTypes } = MojitoServices.Betslip;
const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const { selectOddsFormat } = MojitoServices.UserSettings.selectors;

// eslint-disable-next-line react/display-name
const PrebuiltBet = memo(props => {
    const {
        mojitoTools: { config, l10n },
        event,
        market,
        boostedMarket,
        onEventClick,
        showMarketName,
        children,
        class: className,
    } = props;

    const selection = market?.selections[0];
    const boostedSelection = boostedMarket?.selections[0];
    const parts = market?.preBuiltBetBuilderParts;
    const showAllButtonVisible =
        config.partsThreshold !== -1 && parts?.length > config.partsThreshold;

    const [minimized, setMinimized] = useState(true);

    const onToggleShowAllButton = e => {
        e.stopPropagation();
        setMinimized(!minimized);
    };

    const limitedParts = useMemo(() => {
        if (minimized && config.partsThreshold !== -1) {
            return parts?.slice(0, config.partsThreshold);
        }
        return parts;
    }, [minimized, parts, config.partsThreshold]);

    const selectionsContext = useMemo(
        () => new PrebuiltBetSelectionsContext(market, boostedMarket, event),
        [market, boostedMarket, event]
    );

    const oddsFormat = useSelector(selectOddsFormat);

    const basePrice = selection?.basePrice || boostedSelection?.basePrice;
    const basePriceText = basePrice && EventUtils.getOdds(basePrice, oddsFormat, l10n);

    return (
        <FlexPane
            class={`ta-PrebuiltBet ${className}`.trim()}
            config={config.genericEventCard.container}
        >
            {config.showBackgroundSportImage && <BackgroundSportImage {...props} />}
            <Button
                class="ta-clickableArea"
                onClick={onEventClick}
                disabled={!config.showCardHeader || !config.cardClickable}
                config={config.genericEventCard.clickableArea}
            >
                {children}
                <FlexPane config={config.matchAccaSelectionsContainer}>
                    <MatchAccaSelections
                        showLastChildSeparator={minimized && showAllButtonVisible}
                        parts={limitedParts}
                        showMarketName={showMarketName}
                        l10n={l10n}
                        config={config.matchAccaSelectionItem}
                    />
                </FlexPane>
            </Button>
            {showAllButtonVisible && (
                <ShowAllSelectionsButton
                    config={config.showAllSelectionsButton}
                    minimized={minimized}
                    onMinimizeChange={onToggleShowAllButton}
                />
            )}
            <SelectionsContext.Provider value={selectionsContext}>
                {basePrice ? (
                    <PriceBoostBanner
                        config={config.priceBoostBanner}
                        event={event}
                        market={boostedMarket || market}
                        basePrice={basePriceText}
                        selection={boostedSelection || selection}
                        bettingContext={BetslipTypes.BETTING_CONTEXT.PRE_BUILT_BET}
                    />
                ) : (
                    <FlexPane config={config.selectionContentContainer}>
                        <SelectionButton
                            config={config.selectionButton}
                            selection={selection}
                            event={event}
                            marketStatus={market?.status}
                            marketType={market?.type}
                            marketId={market?.id}
                            bettingContext={BetslipTypes.BETTING_CONTEXT.PRE_BUILT_BET}
                        />
                    </FlexPane>
                )}
            </SelectionsContext.Provider>
        </FlexPane>
    );
});

const BackgroundSportImage = props => {
    const { mojitoTools, event } = props;
    const { config } = mojitoTools;
    return <SportWatermarkImage sportId={event?.sportId} config={config.sportWatermark} />;
};

const MatchAccaSelections = ({ parts, showMarketName, l10n, config, showLastChildSeparator }) =>
    (parts || []).map((part, i, row) => {
        const showSeparator = showLastChildSeparator || i + 1 !== row.length;
        const selectionLabel = BetslipModuleUtils.resolveNameAndHandicapLabel(part, l10n);

        return (
            <MatchAccaSelectionItem
                config={config}
                key={`${part.id}`}
                showSeparator={showSeparator}
                selectionLabel={selectionLabel}
                marketName={showMarketName ? part.marketName : undefined}
                eventName={part.eventName}
                earlyPayout={part.earlyPayout}
            />
        );
    });

export default PrebuiltBet;
